import React, { FC, useCallback, useEffect } from "react";

import { FormDateInput, BusyDayDescription } from "stentor-models";

import dayjs, { Dayjs } from "dayjs";

import { Calendar } from "../Calendar";
import { FormFieldProps } from "../FormDisplay";

import { isBusyDay, parseBusyDays } from "../../utils/busyDays";

import { isDisabledByBusyDays } from "./dateAvailability";

import styles from "./FormFieldDate.module.scss";

export const DEFAULT_BUSY_DAYS: BusyDayDescription = {
    blockWeekends: true,
    blockCurrentDay: true,
    blockNextBusinessDays: 2,
};

export interface FormDateProps extends FormFieldProps {
    /**
     * The date field
     */
    readonly field: FormDateInput;
    /**
     * Busy days as dates comma delimited, for example "YYYY-MM-DD,YYYY-MM-DD"
     */
    readonly busyDays?: string;
    /**
     * Are we loading availability information
     */
    readonly loading: boolean;
}

const loadingMessage = "Getting latest availability...";

export const FormFieldDate: FC<FormDateProps> = (props) => {
    // Only go up to 2 months in the future
    const MAX_DATE = dayjs().add(2, "month");
    const TODAY = dayjs();

    const { field, onFieldChange, errorMessage, busyDays, loading } = props;

    /**
     * Tints a day by whether it can be booked: grey for the past and beyond the two month
     * horizon, green for a day that is open. A day blocked by the busy-day rules keeps no tint.
     */
    const getDayClassName = (day: Dayjs, disabled: boolean): string | undefined => {
        const isPast = day.isBefore(TODAY, "day");

        const isBeyondMaxDate = day.isAfter(MAX_DATE, "day");

        if (isPast || isBeyondMaxDate) {
            return styles.xapp_date_picker_past;
        } else if (disabled) {
            return undefined;
        }

        // available
        return styles.xapp_date_picker_available;
    };

    const [dateTimeValue, setDateTimeValue] = React.useState<Dayjs>();

    const onChange = (newValue: Dayjs) => {
        setDateTimeValue(newValue);
    };

    useEffect(() => {
        onFieldChange(field.name, dateTimeValue);
    }, [dateTimeValue, field.name, onFieldChange]);

    const disabledDays = parseBusyDays(busyDays);

    /**
     * Callback to determine if a date should be disabled used by the calendar
     */
    const shouldDisableDate = useCallback(
        (d: Dayjs): boolean => {
            if (!disabledDays) {
                // See if field has any defaultBusyDays by checking keys
                const hasDefaultBusyDays = Object.keys(field?.defaultBusyDays || {}).length > 0;

                // look for defaultBusyDays on the form or use the DEFAULT_BUSY_DAYS
                const busyDays = hasDefaultBusyDays
                    ? // the extra '|| {}' is to make the subsequent types happy
                      field.defaultBusyDays || {}
                    : DEFAULT_BUSY_DAYS;

                return isDisabledByBusyDays(d, busyDays, TODAY);
            }

            try {
                return isBusyDay(d, disabledDays);
            } catch (err) {
                console.error(`shouldDisableDate: ${err}`);
            }

            return false;
        },
        [TODAY, disabledDays, field.defaultBusyDays]
    );

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignContent: "center",
                alignItems: "center",
            }}
        >
            <div className={styles.xapp_date_picker_container}>
                <Calendar
                    className={styles.xapp_date_picker}
                    classes={{
                        header: styles.xapp_date_picker_header,
                        weekday: styles.xapp_date_picker_weekday,
                        weeks: styles.xapp_date_picker_weeks,
                        day: styles.xapp_date_picker_day,
                        daySelected: styles.xapp_date_picker_day_selected,
                        dayToday: styles.xapp_date_picker_day_today,
                    }}
                    fixedWeekNumber={6}
                    getDayClassName={getDayClassName}
                    loading={loading}
                    maxDate={MAX_DATE}
                    minDate={TODAY}
                    onChange={onChange}
                    shouldDisableDate={shouldDisableDate}
                    showDaysOutsideCurrentMonth={true}
                    today={TODAY}
                    value={dateTimeValue}
                />
            </div>

            {loading && <p className={styles.xapp_date_picker_loading}>{loadingMessage}</p>}

            {!loading && errorMessage && (
                <p className={styles.xapp_date_picker_error}>{errorMessage}</p>
            )}
        </div>
    );
};

export default FormFieldDate;
